
import { supabase } from '../../supabase';
import { Paper } from '@/types/paper';
import { mapToPaper } from '../paper-mappers';

export interface HomepagePapersResult {
  featured: Paper | null;
  recent: Paper[];
  isLoading: boolean;
  error: Error | null;
}

/**
 * Get papers for the homepage - featured paper and recent papers with aggregated counts
 */
export async function getHomepagePapers(limit = 3): Promise<HomepagePapersResult> {
  try {
    console.log('Fetching homepage papers with counts using RPC');
    
    // Call the RPC function that handles filtering and counting at the database level
    const { data, error } = await supabase
      .rpc('get_homepage_papers_with_counts', { recent_limit: limit });
    
    if (error) {
      console.error('Error fetching homepage papers:', error);
      throw error;
    }
    
    if (!data) {
      console.log('No data returned from RPC');
      return {
        featured: null,
        recent: [],
        isLoading: false,
        error: null
      };
    }
    
    // Parse the JSON response
    const { featured: featuredData, recent: recentData } = data;
    
    // Map the results to Paper objects
    const featuredPaper = featuredData ? mapToPaperWithCounts(featuredData, featuredData.versions || []) : null;
    
    const recentPapers: Paper[] = recentData ? recentData.map((paperRow: any) => {
      return mapToPaperWithCounts(paperRow, paperRow.versions || []);
    }) : [];
    
    console.log(`Fetched homepage papers: ${featuredPaper ? 1 : 0} featured, ${recentPapers.length} recent`);
    
    return {
      featured: featuredPaper,
      recent: recentPapers,
      isLoading: false,
      error: null
    };
  } catch (error: any) {
    console.error('Error in getHomepagePapers:', error);
    return {
      featured: null,
      recent: [],
      isLoading: false,
      error
    };
  }
}

/**
 * Enhanced mapper that includes review and comment counts
 */
function mapToPaperWithCounts(paperRow: any, versions: any[]): Paper {
  const basePaper = mapToPaper(paperRow, versions);
  
  // Handle both array format (from direct Supabase queries) and direct numbers (from RPC)
  const reviewCount = typeof paperRow.review_count === 'number' 
    ? paperRow.review_count 
    : (paperRow.review_count?.[0]?.count || 0);
    
  const commentCount = typeof paperRow.comment_count === 'number'
    ? paperRow.comment_count
    : (paperRow.comment_count?.[0]?.count || 0);
  
  return {
    ...basePaper,
    reviewCount,
    commentCount,
    averageRating: paperRow.average_rating ? Number(paperRow.average_rating) : undefined
  };
}
